'use client';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { PropertyFilter } from '@src/v3/interfaces/utils/http';
import SearchAutocomplete from '@src/v3/components/searchAutocomplete';
import Search from '@src/v3/assets/svg/search';
import { AddressAutoSearch } from '@src/v3/interfaces/components/searchAutocomplete';
import { useSearchParams, usePathname } from 'next/navigation';
// import AgentPublicProfileCard from '@src/components/property/agentProfile';
import Button from '@src/v3/components/coreUI/button';
import Icon from '@src/v3/components/coreUI/icon';
import ListingsFilter from '@src/v3/components/listingsFilter';
import {
  getSearchDefaultValue,
  getSearchDefaultType,
} from '@src/v3/utils/searchHelpers';

type SubHeaderWithPropertyProps = {
  // eslint-disable-next-line no-unused-vars
  applyFilters: (filters: PropertyFilter) => void;
  // eslint-disable-next-line no-unused-vars
  handleSearchChange: (address: AddressAutoSearch[] | null) => void;
  resetFilters: () => void;
  isHideSearch?: boolean;
  isHideFiltering?: boolean;
};

const SubHeaderWithProperty = ({
  applyFilters = () => {
    return;
  },
  handleSearchChange = () => {
    return;
  },
  resetFilters = () => {
    return;
  },
  isHideSearch = false,
  isHideFiltering = false, // userIdentifier = '',
}: SubHeaderWithPropertyProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);
  const [focusKey, setFocusKey] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

  // Check if it's a public profile page
  const isPublicProfilePage = pathname?.includes('/profile/');
  // const [searchInputRef, setSearchInputRef] = useState<HTMLDivElement | null>(
  //   null
  // );
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [animationClass, setAnimationClass] = useState<string>('');

  // States
  const [defaultSearchValue, setDefaultSearchValue] = useState(
    getSearchDefaultValue(searchParams)
  );
  const [searchType, setSearchType] = useState('');

  useEffect(() => {
    const type = getSearchDefaultType(searchParams);
    setSearchType(type);
  }, [searchParams]);

  // Check scroll position and update button visibility
  const updateScrollButtons = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Update scroll buttons on mount and when search expands
  useEffect(() => {
    updateScrollButtons();
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      // Also check on resize
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
    return undefined;
  }, [updateScrollButtons, isSearchExpanded]);

  // Scroll handling functions
  const handleScrollRight = () => {
    const container = containerRef.current;
    if (container) {
      const scrollAmount = 220;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleScrollLeft = () => {
    const container = containerRef.current;
    if (container) {
      const scrollAmount = 220;
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const handleSearchButtonClick = () => {
    if (isAnimating) return; // Prevent multiple clicks during animation

    // Increment focusKey to trigger autoFocus on remount
    setFocusKey((prev) => prev + 1);

    setIsAnimating(true);
    setAnimationClass('search-expand-enter');

    // Small delay to show button press effect
    setTimeout(() => {
      setIsSearchExpanded(true);
      setAnimationClass('search-input-enter');

      // Reset animation state after animation completes
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationClass('');
      }, 300);
    }, 100);
  };

  const handleSearchBlur = (hasValue: boolean) => {
    // Only collapse if the search has no value
    if (hasValue) return;

    if (isAnimating) return; // Prevent blur during animation

    setIsAnimating(true);
    setAnimationClass('search-input-exit');

    // Delay collapse to allow for selection and show exit animation
    setTimeout(() => {
      setIsSearchExpanded(false);
      setAnimationClass('search-collapse-exit');

      setTimeout(() => {
        setIsAnimating(false);
        setAnimationClass('');
      }, 200);
    }, 200);
  };

  const handleSearchSelect = (addresses: AddressAutoSearch[] | null) => {
    const totalAddress =
      addresses?.map(({ main_text = '' }) => {
        return {
          address: main_text || '',
          main_text: main_text || '',
          place_id: main_text || '',
          secondary_text: '',
          types: 'neighborhood',
        };
      }) ?? [];
    setDefaultSearchValue(totalAddress);
    handleSearchChange(addresses);
  };

  return (
    <>
      <div className="sticky z-10 bg-white" id="homeSubNav">
        <h3 className="text-2xl pb-3 font-semibold tracking-[-0.72px]">
          {isPublicProfilePage ? 'Listings' : 'Welcome to Zeme!'}
        </h3>

        {/* New Filter Layout */}
        <div className="relative">
          {/* Left Scroll Button */}
          {canScrollLeft && (
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 z-[999]">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleScrollLeft}
                className="h-9 w-9 rounded-full bg-white shadow-md border border-gray-200 hover:bg-gray-50 p-0"
              >
                <Icon
                  iconName="chevronLeft"
                  iconProps={{ className: 'h-3 w-3' }}
                />
              </Button>
            </div>
          )}

          {/* Container with hidden scrollbar */}
          <div
            ref={containerRef}
            id="filter-container"
            className="flex items-center gap-3 overflow-x-auto scrollbar-hide"
            style={{
              scrollbarWidth: 'none' /* Firefox */,
              msOverflowStyle: 'none' /* IE and Edge */,
            }}
          >
            {/* Search Input */}
            {!isHideSearch ? (
              <div
                // ref={setSearchInputRef}
                className={`relative flex-shrink-0 pt-2  ${
                  isSearchExpanded
                    ? 'w-[400px] sm:w-[420px] transition-all duration-500 ease-out'
                    : 'w-auto transition-all duration-300 ease-in-out'
                }`}
                // onBlur={handleSearchBlur}
              >
                {!isSearchExpanded ? (
                  <div>
                    <Button
                      variant="ghost"
                      onClick={handleSearchButtonClick}
                      disabled={isAnimating}
                      className={`h-14 p-4 flex items-center bg-gray-lighter hover:bg-gray-light rounded-[100px] gap-x-3 text-base text-gray-900 hover:text-gray-900 font-semibold transition-all duration-200 border-2 ${
                        defaultSearchValue?.length > 0
                          ? 'border-primary-dark'
                          : 'border-transparent'
                      } ${animationClass}`}
                    >
                      <Icon
                        iconName="searchIcon"
                        iconProps={{
                          className: `w-6 h-6 `,
                        }}
                      />
                      <span
                        className={`transition-opacity duration-100 ${
                          isAnimating ? 'opacity-0' : 'opacity-100'
                        }`}
                      >
                        Location
                      </span>
                    </Button>
                    {defaultSearchValue.length > 0 && (
                      <div className="absolute top-1 right-0 w-5 h-5 border-2 border-white bg-primary-dark rounded-full flex items-center justify-center z-10"></div>
                    )}
                  </div>
                ) : (
                  // Expanded state - shows SearchAutocomplete
                  <div className={`hidden sm:block relative ${animationClass}`}>
                    <div className="flex absolute left-5 z-[999] items-center h-full w-4">
                      <span className="flex justify-center items-center w-6 h-6 text-gray-900">
                        <Search />
                      </span>
                    </div>
                    <div className="relative z-[100] pl-1">
                      <SearchAutocomplete
                        key={focusKey}
                        defaultInputValue=""
                        defaultValue={defaultSearchValue}
                        searchType={searchType}
                        onSelect={handleSearchSelect}
                        onBlur={handleSearchBlur}
                        autoFocus
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {/* Filter Buttons */}
            {!isHideFiltering ? (
              <ListingsFilter
                applyFilters={applyFilters}
                resetFilters={resetFilters}
              />
            ) : null}
          </div>

          {/* Right Scroll Button */}
          {canScrollRight && (
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleScrollRight}
                className="h-9 w-9 rounded-full bg-white shadow-md border border-gray-200 hover:bg-gray-50 p-0"
              >
                <Icon
                  iconName="chevronRight"
                  iconProps={{ className: 'h-3 w-3' }}
                />
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SubHeaderWithProperty;
